import React, { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useCatalog } from './providers/CatalogProvider.jsx';

const SITE_ORIGIN = 'https://watchvim.com';
const DEFAULT_IMAGE = `${SITE_ORIGIN}/assets/share-1200x630.jpg`;
const PUBLIC_ROBOTS = 'index,follow,max-image-preview:large,max-video-preview:-1,max-snippet:-1';
const PRIVATE_ROBOTS = 'noindex,follow,noarchive';

const STATIC_META = {
  '/home': ['WatchVIM | Independent Films, Series & Originals', 'Stream independent films, series, shorts, and WatchVIM Originals. Discover bold stories and new voices on WatchVIM.'],
  '/movies': ['Movies on WatchVIM', 'Browse independent feature films, documentaries, shorts, and bold cinema available on WatchVIM.'],
  '/shows': ['Shows & Series on WatchVIM', 'Discover independent series, episodic stories, and WatchVIM Originals available to stream on WatchVIM.'],
  '/originals': ['WatchVIM Originals', 'Explore WatchVIM Original films, series, shorts, and independent productions created for the WatchVIM audience.'],
  '/shorts': ['Short Films on WatchVIM', 'Browse short films and short-form stories available on WatchVIM.'],
  '/membership': ['WatchVIM Membership | Stream Independent Films & Series', 'Explore WatchVIM membership options for streaming independent films, series, shorts, and WatchVIM Originals on demand.'],
  '/subscribe': ['Subscribe to WatchVIM | Independent Films & Original Series', 'Subscribe to WatchVIM for $5.99/month and stream independent films, Black stories, LGBTQIA+ voices, original series, and bold new creators.'],
  '/about': ['About WatchVIM', 'Learn about WatchVIM, an independent streaming destination for films, series, shorts, originals, and emerging storytellers.'],
  '/press': ['WatchVIM Newsroom | Press & Media', 'Official WatchVIM announcements, company news, and media information from VIM Media.'],
  '/partners': ['WatchVIM Partnerships', 'Explore content, advertising, sponsorship, production, and strategic partnership opportunities with WatchVIM.'],
  '/distribution': ['Film & Series Distribution | WatchVIM', 'Learn how independent filmmakers and rights holders can submit films, series, shorts, and documentaries for WatchVIM distribution consideration.'],
  '/faq': ['WatchVIM Frequently Asked Questions', 'Get answers about WatchVIM streaming, accounts, subscriptions, rentals, filmmaker submissions, advertising, and support.'],
  '/refund-policy': ['Refund Policy | WatchVIM', 'Review WatchVIM refund, cancellation, subscription, rental, and digital transaction policies.'],
  '/accessibility': ['Accessibility | WatchVIM', 'Learn about WatchVIM accessibility efforts for the streaming website, video experience, and customer support.'],
  '/dmca': ['Copyright & DMCA Notices | WatchVIM', 'Review WatchVIM copyright and DMCA notice information for reporting claimed infringement.'],
  '/privacy': ['Privacy Policy | WatchVIM', 'Read the WatchVIM Privacy Policy and learn how VIM Media handles data, cookies, advertising, and privacy choices.'],
  '/cookie-policy': ['Cookie Policy | WatchVIM', 'Learn how WatchVIM uses essential, analytics, preference, advertising, and measurement technologies.'],
  '/terms': ['Terms and Conditions | WatchVIM', 'Read the WatchVIM Terms and Conditions for streaming, subscriptions, rentals, purchases, accounts, and platform use.'],
  '/support': ['WatchVIM Support', 'Contact WatchVIM Support for account, subscription, payment, playback, filmmaker, advertising, or technical assistance.'],
  '/contact': ['Contact WatchVIM', 'Contact WatchVIM for streaming support, filmmaker distribution, advertising, partnerships, or general questions.'],
  '/advertise': ['Advertise on WatchVIM', 'Explore WatchVIM video advertising, display advertising, sponsorship, and campaign opportunities.'],
  '/submit': ['Submit Your Film or Series | WatchVIM', 'Submit an independent film, series, short, or documentary for WatchVIM distribution consideration.'],
  '/casting': ['WatchVIM Casting', 'View current WatchVIM casting opportunities and notices for productions seeking talent.'],
  '/careers': ['Careers at WatchVIM', 'Explore jobs, internships, freelance opportunities, and production work with WatchVIM and VIM Media.'],
};

// Keep public discovery/acquisition pages indexable while preventing account,
// checkout, playback, and private worker/filmmaker workflows from entering search.
const NOINDEX_PREFIXES = [
  '/login', '/signup', '/forgot-password', '/reset-password', '/verify-email',
  '/profile', '/checkout', '/rent/', '/watch/', '/trailer/', '/filmmakers', '/pay',
  '/continue-watching', '/search',
  '/activate', '/tv/activate', '/tv-activate', '/roku/activate', '/redeem-code',
  '/submit/', '/advertise/video', '/advertise/display', '/advertise/dashboard',
];

function first(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function decode(value = '') {
  try { return decodeURIComponent(value); } catch (_) { return value; }
}

function absoluteUrl(value) {
  const raw = String(value || '').trim();
  if (!raw) return DEFAULT_IMAGE;
  if (/^https?:\/\//i.test(raw)) return raw;
  return `${SITE_ORIGIN}${raw.startsWith('/') ? '' : '/'}${raw}`;
}

function trimDescription(value, fallback) {
  const raw = String(value || fallback || '').replace(/\s+/g, ' ').trim();
  if (raw.length <= 158) return raw;
  return `${raw.slice(0, 155).replace(/\s+\S*$/, '')}…`;
}

function list(value) {
  if (Array.isArray(value)) return value;
  if (value === undefined || value === null || value === '') return [];
  if (typeof value === 'object') return [value];
  return String(value).split(/[,|;]/).map((entry) => entry.trim()).filter(Boolean);
}

function people(value) {
  const result = list(value).map((entry) => {
    const name = typeof entry === 'string' ? entry.trim() : String(first(entry?.name, entry?.fullName, entry?.displayName, entry?.title, '')).trim();
    return name ? { '@type': 'Person', name } : null;
  }).filter(Boolean);
  return result.length ? result : undefined;
}

function durationIso(value) {
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value === 'string' && /^P(T|\d)/i.test(value.trim())) return value.trim();
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return undefined;
  const minutes = Math.round(number);
  return `PT${minutes}M`;
}

function validDate(value) {
  const raw = String(value || '').trim();
  if (!raw) return undefined;
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, 10);
}

function titleById(catalog, id) {
  return catalog?.titles?.find((item) => item.id === id || item.slug === id) || null;
}

function hasSynopsis(item) {
  return Boolean(String(item?.description || '').trim());
}

function routeTarget(pathname) {
  const parts = pathname.split('/').filter(Boolean).map(decode);
  if (parts[0] === 'title' && parts[1] && parts.length === 2) return { kind: 'title', id: parts[1] };
  if (parts[0] === 'series' && parts[1]) return { kind: 'series', id: parts[1], season: parts[3] || '' };
  if (parts[0] === 'episode' && parts[1] && parts[2] && parts[3]) return { kind: 'episode', id: parts[1], season: parts[2], episode: parts[3] };
  return null;
}

function titleStructuredData(item, canonical) {
  const raw = item?.raw || {};
  const type = item.kind === 'series' ? 'TVSeries' : 'Movie';
  const releaseDate = validDate(first(raw.releaseDate, raw.release_date, raw.premiereDate, raw.premiere_date, raw.originalReleaseDate, raw.original_release_date));
  const cast = people(first(raw.cast, raw.actors, raw.starring, raw.stars));
  const directors = people(first(raw.directors, raw.director, raw.directedBy, raw.directed_by));
  const creators = people(first(raw.creators, raw.creator, raw.createdBy, raw.created_by));
  const productionName = String(first(raw.productionCompany, raw.production_company, raw.studio, raw.network, raw.label, '')).trim();
  const language = first(raw.language, raw.inLanguage, raw.audioLanguage, raw.audio_language);
  const rating = first(raw.contentRating, raw.content_rating, raw.maturityRating, raw.maturity_rating, raw.mpaaRating, raw.mpaa_rating, raw.rating);
  const seasons = item.kind === 'series' ? [...new Set((item.episodes || []).map((entry) => String(entry.season || '')).filter(Boolean))] : [];

  return compact({
    '@context': 'https://schema.org',
    '@type': type,
    '@id': canonical,
    url: canonical,
    name: item.title,
    description: item.description || undefined,
    image: absoluteUrl(item.backdrop || item.image),
    datePublished: releaseDate,
    genre: item.genres?.length ? item.genres : undefined,
    duration: item.kind === 'series' ? undefined : durationIso(item.duration),
    contentRating: rating ? String(rating) : undefined,
    inLanguage: language ? String(language) : undefined,
    actor: cast,
    director: directors,
    creator: creators,
    productionCompany: productionName ? { '@type': 'Organization', name: productionName } : undefined,
    numberOfEpisodes: item.kind === 'series' && item.episodes?.length ? item.episodes.length : undefined,
    numberOfSeasons: item.kind === 'series' && seasons.length ? seasons.length : undefined,
  });
}

function episodeStructuredData(series, item, canonical) {
  const raw = item?.raw || {};
  return compact({
    '@context': 'https://schema.org',
    '@type': 'TVEpisode',
    '@id': canonical,
    url: canonical,
    name: item.title,
    description: item.description || undefined,
    image: absoluteUrl(item.image || series.backdrop || series.image),
    duration: durationIso(item.duration),
    episodeNumber: Number(item.episode) || undefined,
    datePublished: validDate(first(raw.releaseDate, raw.release_date, raw.airDate, raw.air_date, raw.premiereDate, raw.premiere_date)),
    actor: people(first(raw.cast, raw.actors, raw.starring, raw.stars)),
    director: people(first(raw.directors, raw.director, raw.directedBy, raw.directed_by)),
    partOfSeason: {
      '@type': 'TVSeason',
      seasonNumber: Number(item.season) || item.season,
      partOfSeries: { '@type': 'TVSeries', name: series.title, url: `${SITE_ORIGIN}/series/${encodeURIComponent(series.id)}` },
    },
  });
}

function compact(value) {
  if (Array.isArray(value)) {
    const result = value.map(compact).filter((entry) => entry !== undefined && entry !== null && entry !== '');
    return result.length ? result : undefined;
  }
  if (!value || typeof value !== 'object') return value;
  const output = {};
  Object.entries(value).forEach(([key, entry]) => {
    const next = compact(entry);
    if (next !== undefined && next !== null && next !== '') output[key] = next;
  });
  return Object.keys(output).length ? output : undefined;
}

function homeStructuredData() {
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        '@id': `${SITE_ORIGIN}/#organization`,
        name: 'WatchVIM',
        url: SITE_ORIGIN,
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_ORIGIN}/#website`,
        url: SITE_ORIGIN,
        name: 'WatchVIM',
        publisher: { '@id': `${SITE_ORIGIN}/#organization` },
        potentialAction: {
          '@type': 'SearchAction',
          target: `${SITE_ORIGIN}/search?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      },
    ],
  };
}

function subscribeStructuredData() {
  const canonical = `${SITE_ORIGIN}/subscribe`;
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: 'Subscribe to WatchVIM',
        description: STATIC_META['/subscribe'][1],
        isPartOf: { '@id': `${SITE_ORIGIN}/#website` },
        about: { '@id': `${canonical}#service` },
      },
      {
        '@type': 'Service',
        '@id': `${canonical}#service`,
        name: 'WatchVIM Streaming Membership',
        serviceType: 'Subscription video on demand streaming',
        provider: {
          '@type': 'Organization',
          '@id': `${SITE_ORIGIN}/#organization`,
          name: 'WatchVIM',
          url: SITE_ORIGIN,
        },
        offers: {
          '@type': 'Offer',
          url: canonical,
          price: '5.99',
          priceCurrency: 'USD',
          category: 'Monthly streaming membership',
          availability: 'https://schema.org/InStock',
        },
      },
    ],
  };
}

function setMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', href);
}

function setJsonLd(data) {
  document.head.querySelectorAll('script[data-watchvim-seo-jsonld="true"]').forEach((node) => node.remove());
  if (!data) return;
  const script = document.createElement('script');
  script.type = 'application/ld+json';
  script.dataset.watchvimSeoJsonld = 'true';
  script.textContent = JSON.stringify(data).replace(/</g, '\\u003c');
  document.head.appendChild(script);
}

function staticMetadata(pathname) {
  const match = STATIC_META[pathname];
  if (!match) return null;
  return {
    title: match[0],
    description: match[1],
    canonical: `${SITE_ORIGIN}${pathname === '/home' ? '/' : pathname}`,
    image: DEFAULT_IMAGE,
    type: 'website',
    robots: PUBLIC_ROBOTS,
    structured: pathname === '/home' ? homeStructuredData() : pathname === '/subscribe' ? subscribeStructuredData() : null,
  };
}

function isNoIndexPath(pathname) {
  return NOINDEX_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(prefix));
}

export function SeoMetadata() {
  const { pathname } = useLocation();
  const { catalog, status } = useCatalog();

  const metadata = useMemo(() => {
    const staticMatch = staticMetadata(pathname);
    if (staticMatch) {
      const collection = { '/home': 'titles', '/movies': 'movies', '/shows': 'shows', '/originals': 'originals', '/shorts': 'shorts' }[pathname];
      if (collection && (status !== 'ready' || !catalog?.[collection]?.length)) {
        return { ...staticMatch, robots: PRIVATE_ROBOTS, structured: null };
      }
      return staticMatch;
    }

    if (pathname.startsWith('/genres/')) {
      const requested = decode(pathname.slice('/genres/'.length)).replace(/-/g, ' ');
      const items = (catalog?.titles || []).filter((item) => item.genres?.some((genre) => genre.toLowerCase() === requested.toLowerCase()));
      const name = items[0]?.genres.find((genre) => genre.toLowerCase() === requested.toLowerCase()) || requested;
      return {
        title: `${name || 'Genres'} | WatchVIM`,
        description: `Browse ${name || 'genre'} films and series in the WatchVIM catalog.`,
        canonical: `${SITE_ORIGIN}/genres/${encodeURIComponent(name)}`,
        image: DEFAULT_IMAGE,
        type: 'website',
        robots: status === 'ready' && items.length ? PUBLIC_ROBOTS : PRIVATE_ROBOTS,
        structured: null,
      };
    }

    const target = routeTarget(pathname);
    if (target && status === 'ready') {
      const seriesOrTitle = titleById(catalog, target.id);
      if (seriesOrTitle) {
        if (target.kind === 'episode') {
          const episode = seriesOrTitle.episodes?.find((entry) => String(entry.season) === String(target.season) && String(entry.episode) === String(target.episode));
          if (episode) {
            const canonical = `${SITE_ORIGIN}/episode/${encodeURIComponent(seriesOrTitle.id)}/${encodeURIComponent(episode.season)}/${encodeURIComponent(episode.episode)}`;
            return {
              title: `${episode.title} | ${seriesOrTitle.title} | WatchVIM`,
              description: trimDescription(episode.description, `Watch ${episode.title}, Season ${episode.season} Episode ${episode.episode} of ${seriesOrTitle.title} on WatchVIM.`),
              canonical,
              image: absoluteUrl(episode.image || seriesOrTitle.backdrop || seriesOrTitle.image),
              type: 'video.episode',
              robots: hasSynopsis(episode) ? PUBLIC_ROBOTS : PRIVATE_ROBOTS,
              structured: hasSynopsis(episode) ? episodeStructuredData(seriesOrTitle, episode, canonical) : null,
            };
          }
        } else {
          const canonical = `${SITE_ORIGIN}/${seriesOrTitle.kind === 'series' ? 'series' : 'title'}/${encodeURIComponent(seriesOrTitle.id)}`;
          return {
            title: `${seriesOrTitle.title} | Watch on WatchVIM`,
            description: trimDescription(seriesOrTitle.description, `Watch ${seriesOrTitle.title} on WatchVIM. Explore details, availability, and related independent stories.`),
            canonical,
            image: absoluteUrl(seriesOrTitle.backdrop || seriesOrTitle.image),
            type: seriesOrTitle.kind === 'series' ? 'video.tv_show' : 'video.movie',
            robots: hasSynopsis(seriesOrTitle) ? PUBLIC_ROBOTS : PRIVATE_ROBOTS,
            structured: hasSynopsis(seriesOrTitle) ? titleStructuredData(seriesOrTitle, canonical) : null,
          };
        }
      }
    }

    // Missing/loading/error detail pages and unknown routes have no verified publisher content.
    const noindex = isNoIndexPath(pathname) || Boolean(target) || !staticMatch;
    return {
      title: 'WatchVIM',
      description: 'WatchVIM — Cinema. Culture. On Demand.',
      canonical: `${SITE_ORIGIN}${pathname || '/'}`,
      image: DEFAULT_IMAGE,
      type: 'website',
      robots: noindex ? PRIVATE_ROBOTS : PUBLIC_ROBOTS,
      structured: null,
    };
  }, [pathname, catalog, status]);

  useEffect(() => {
    document.title = metadata.title;
    setMeta('meta[name="description"]', { name: 'description', content: metadata.description });
    setMeta('meta[name="robots"]', { name: 'robots', content: metadata.robots });
    setMeta('meta[name="googlebot"]', { name: 'googlebot', content: metadata.robots });
    setMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: 'WatchVIM' });
    setMeta('meta[property="og:title"]', { property: 'og:title', content: metadata.title });
    setMeta('meta[property="og:description"]', { property: 'og:description', content: metadata.description });
    setMeta('meta[property="og:url"]', { property: 'og:url', content: metadata.canonical });
    setMeta('meta[property="og:type"]', { property: 'og:type', content: metadata.type });
    setMeta('meta[property="og:image"]', { property: 'og:image', content: metadata.image });
    setMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    setMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: metadata.title });
    setMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: metadata.description });
    setMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: metadata.image });
    setCanonical(metadata.canonical);
    setJsonLd(metadata.structured);
  }, [metadata]);

  return null;
}

